/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Badge, Container, Table } from "reactstrap";
import { getNotifications, updateFilter } from "../../../actions/Mailbox/actions";
import { MailboxState } from "../../../actions/Mailbox/reducer";
import { usePagination } from "../../../hooks";
import { RootState } from "../../../reducers";
import { AttachmentLink } from "../../Helper/AttachmentLink";
import { Loading } from "../../Helper/Loading";
import { PaginationRow } from "../../Helper/PaginationRow";
import { TableCell, TableHeaderCell } from "../../Helper/Table";
import { MailRecipients } from "../Helpers";
import { formatDateTime } from "../Layers/fields/formatters";
import FullPageLayout from "../Layout/FullPageLayout";


export default function MailboxPage() {
    const { body } = mailboxComponents();
    return <Container className="py-md-5 py-3">
        {body}
    </Container>;

}

export function MailboxTab() {
    const { body } = mailboxComponents();
    return body;
}
const MailStatus = {
    New: "new",
    InProgress: "in_progress",
    Sent: "sent",
    Failed: "failed",
    Retry: "retry"
};

const BadgeColors = {
    [MailStatus.New]: "primary",
    [MailStatus.InProgress]: "warning",
    [MailStatus.Sent]: "success",
    [MailStatus.Failed]: "danger",
};

const getBadgeColor = (status: string) => {
    const color = BadgeColors[status];
    return color || "secondary";
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const StatusBadge = ({ status }) => {
    status = status === MailStatus.Retry ? MailStatus.InProgress : status;
    const badgeColor = getBadgeColor(status);
    const statusToPrint = capitalize(status.replace("_", " "));
    return <Badge color={badgeColor}>{statusToPrint}</Badge>;
};

const mailboxComponents = () => {
    const dispatch = useDispatch();
    const items = useSelector((state: any) => state.mailbox.items);
    const isLoading = useSelector((state: any) => state.mailbox.isLoading);
    const filter = useSelector<RootState, MailboxState["filter"]>((state) => state.mailbox.filter);

    const { offset, limit } = usePagination();

    useEffect(() => {
        if (filter.offset !== offset || filter.limit !== limit) {
            dispatch(updateFilter({ ...filter, offset, limit }));
        }
    }, [offset, limit]);

    useEffect(() => {
        dispatch(getNotifications(filter));
    }, [filter]);

    const body = (
        <FullPageLayout withStyledBox>
            <div className="mt-5">
                <div className="d-flex flex-column" css={css`min-height: 50vh;`} >
                    {isLoading
                        ? <Loading />
                        : (
                            <Table size="sm" responsive={true} borderless={true}>
                                <thead>
                                    <tr>
                                        <TableHeaderCell>Subject</TableHeaderCell>
                                        <TableHeaderCell>Layer</TableHeaderCell>
                                        <TableHeaderCell>Supplier</TableHeaderCell>
                                        <TableHeaderCell>Report</TableHeaderCell>
                                        <TableHeaderCell>Recipients</TableHeaderCell>
                                        <TableHeaderCell>Status</TableHeaderCell>
                                        <TableHeaderCell>Created</TableHeaderCell>
                                    </tr>
                                </thead>

                                <tbody>
                                    {items.results.map((item) => {
                                        const { supplier, layer } = item.attributes;
                                        return <tr key={item?.id}>
                                            <TableCell>{item.subject}</TableCell>
                                            <TableCell>
                                                {layer && layer.id
                                                    ? <Link to={`/layer/${layer.id}`}>{layer.label || layer.id}</Link>
                                                    : "-"}
                                            </TableCell>
                                            <TableCell>{supplier?.name || "-"}</TableCell>
                                            <TableCell>
                                                {item.attachment_urls.map((url, i) => <AttachmentLink key={i} url={url} />)}
                                            </TableCell>
                                            <TableCell><MailRecipients recipients={item.recipients} /></TableCell>
                                            <TableCell><StatusBadge status={item.status} /></TableCell>
                                            <TableCell>{formatDateTime(dayjs(item.created))}</TableCell>
                                        </tr>;
                                    })}
                                </tbody>
                            </Table>
                        )}
                </div>

                <PaginationRow totalCount={items.count} />
            </div>
        </FullPageLayout>
    );

    return { body };
};


